import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Section, CATEGORY_LABELS, MAX_TOTAL_PAGES } from "@/data/agendaData";
import { Info } from "lucide-react";

interface SectionSelectorProps {
  sections: Section[];
  selectedSectionIds: number[];
  onSectionsChange: (sectionIds: number[]) => void;
}

export const SectionSelector = ({
  sections,
  selectedSectionIds,
  onSectionsChange,
}: SectionSelectorProps) => {
  // Calcular páginas totales seleccionadas
  const selectedSections = sections.filter((s) => selectedSectionIds.includes(s.id));
  const totalPagesUsed = selectedSections.reduce((sum, section) => sum + section.pages, 0);
  const pagesRemaining = MAX_TOTAL_PAGES - totalPagesUsed;

  const handleToggle = (sectionId: number) => {
    const isSelected = selectedSectionIds.includes(sectionId);
    const section = sections.find((s) => s.id === sectionId);

    if (!section) return;

    if (isSelected) {
      // Siempre permitir deseleccionar
      onSectionsChange(selectedSectionIds.filter((id) => id !== sectionId));
    } else {
      // Solo permitir seleccionar si hay páginas suficientes
      if (pagesRemaining >= section.pages) {
        onSectionsChange([...selectedSectionIds, sectionId]);
      }
    }
  };

  // Agrupar secciones por categoría
  const groupedSections = sections.reduce((acc, section) => {
    if (!acc[section.category]) {
      acc[section.category] = [];
    }
    acc[section.category].push(section);
    return acc;
  }, {} as Record<string, Section[]>);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold mb-2">2. Selecciona tus Secciones</h2>
        <p className="text-muted-foreground">
          Tu agenda tiene un máximo de {MAX_TOTAL_PAGES} páginas. Cada sección que elijas usa parte del total.
          Entre menos secciones, más hojas quedarán para tu organización diaria.
        </p>
      </div>

      {/* Contador de páginas */}
      <Alert className={pagesRemaining > 0 ? "border-primary" : "border-orange-500"}>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {totalPagesUsed} de {MAX_TOTAL_PAGES} páginas usadas
            </span>
            <span className={`font-bold ${pagesRemaining < 50 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
              {pagesRemaining} páginas restantes
            </span>
          </div>
        </AlertDescription>
      </Alert>

      {/* Secciones agrupadas por categoría */}
      <div className="space-y-6">
        {Object.entries(groupedSections).map(([category, categorySections]) => (
          <div key={category}>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
              <Badge variant="outline" className="text-xs">
                {categorySections.filter((s) => selectedSectionIds.includes(s.id)).length}/
                {categorySections.length}
              </Badge>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {categorySections.map((section) => {
                const isSelected = selectedSectionIds.includes(section.id);
                const isDisabled = !isSelected && pagesRemaining < section.pages;

                return (
                  <Card
                    key={section.id}
                    className={`cursor-pointer transition-all ${
                      isSelected
                        ? "ring-2 ring-primary shadow-md"
                        : isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => !isDisabled && handleToggle(section.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          className="mt-1"
                          onCheckedChange={() => !isDisabled && handleToggle(section.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <CardTitle className="text-base flex items-center gap-2">
                            <span className="text-xl">{section.icon}</span>
                            {section.name}
                          </CardTitle>
                          <CardDescription className="text-xs mt-1">
                            {section.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0 pb-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          {section.pages} páginas
                        </span>
                        <span className="font-bold text-primary">
                          +${section.price.toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
